import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { endpoints } from '@rossum/api-client';
import AnnotationsActionModal from '../../../../../components/AnnotationsActionModal';
import { notify } from '../../../../../features/toaster';
import { api } from '../../../../../lib/apiClient';
import { handleTooManyImportsError } from '../../../../../lib/errorHandlers';
import { displayAnnotation } from '../../../../../redux/modules/annotation/actions';
import {
  buildReExtractPayload,
  isReExtractable,
} from '../../../../../redux/modules/annotation/helpers';
import { userSelector } from '../../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../../types/annotation';
import { SidebarFooterAction } from './utils';

export const useReExtractAction = (
  annotation: Annotation
): { action: SidebarFooterAction | null; modal: JSX.Element | null } => {
  const intl = useIntl();

  const { status, id, modifier, restrictedAccess } = annotation;

  const currentUserUrl = useSelector(userSelector).url;

  const dispatch = useDispatch();

  const { mutate: reExtractAnnotation } = useMutation({
    mutationFn: () => {
      const payload = buildReExtractPayload();
      return api.request(endpoints.annotations.patch(id, payload));
    },
    onSuccess: () => {
      dispatch(displayAnnotation({ id: annotation.id, mode: 'refresh' }));
    },
    onError: (error: unknown) => {
      try {
        handleTooManyImportsError(error, intl);
      } catch {
        notify.error({
          title: intl.formatMessage({
            id: 'components.annotationOverview.actionButtons.reExtract.error',
          }),
        });
      }
    },
  });

  const [modalOpen, setModalOpen] = useState(false);

  const disabled =
    restrictedAccess || !isReExtractable({ status, modifier, currentUserUrl });

  const handleConfirm = useCallback(() => {
    setModalOpen(false);
    reExtractAnnotation();
  }, [reExtractAnnotation]);

  const handleClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const result = useMemo(() => {
    return {
      modal: modalOpen ? (
        <AnnotationsActionModal
          action="reExtract"
          annotationsLength={1}
          onClose={() => setModalOpen(false)}
          onPrimaryActionClick={handleConfirm}
          dataCyConfirm="reextract-dialog-confirm-btn"
          dataCyCancel="reextract-dialog-cancel-btn"
        />
      ) : null,
      action: {
        id: 'reExtract' as const,
        label: intl.formatMessage({
          id: 'containers.annotationList.selectionPanel.buttons.reExtract',
        }),
        disabled,
        onClick: handleClick,
        dataCy: 'annotation-sidebar-reextract',
      },
    };
  }, [intl, disabled, handleClick, modalOpen, handleConfirm]);

  return result;
};
